import React, { useRef, useState } from 'react';
import { files as filesApi } from '../services/api';
import { toast } from '../hooks/use-toast';
import { Loader2, ImagePlus, Trash2 } from 'lucide-react';

// Small reusable file-upload widget. Shows an upload dropzone when empty,
// preview + Replace/Remove controls when set. `value` is a URL string.
export default function ImagePicker({ value, onChange, kind = 'post', testid = 'image', label }) {
  const [uploading, setUploading] = useState(false);
  const ref = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) { toast({ title: 'Please pick an image file' }); return; }
    if (file.size > 10 * 1024 * 1024)     { toast({ title: 'Image is too large (max 10MB)' }); return; }
    setUploading(true);
    try {
      const r = await filesApi.upload(file, kind);
      onChange(r.url);
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || 'Try again' });
    } finally {
      setUploading(false);
      if (ref.current) ref.current.value = '';
    }
  };

  return (
    <div>
      <input ref={ref} type="file" accept="image/*" className="hidden"
        data-testid={`${testid}-file`}
        onChange={(e) => upload(e.target.files?.[0])}/>
      {!value ? (
        <button type="button" onClick={() => ref.current?.click()}
          disabled={uploading}
          data-testid={`${testid}-upload`}
          className="w-full flex items-center justify-center gap-2 px-3 py-3 rounded-lg border border-dashed border-[#3a3a3a] hover:border-green-500 bg-black text-sm text-gray-300 hover:text-white disabled:opacity-60">
          {uploading ? <><Loader2 size={14} className="animate-spin"/> Uploading…</> : <><ImagePlus size={14}/> {label || 'Upload image'}</>}
        </button>
      ) : (
        <div className="relative rounded-lg overflow-hidden border border-[#2a2a2a] bg-black" data-testid={`${testid}-preview`}>
          <img src={filesApi.urlFor(value)} alt="preview" className="w-full h-40 object-cover"/>
          <div className="absolute top-2 right-2 flex gap-2">
            <button type="button" onClick={() => ref.current?.click()} disabled={uploading}
              data-testid={`${testid}-replace`}
              className="text-[11px] px-2 py-1 rounded bg-black/70 border border-[#3a3a3a] text-white hover:border-green-500 inline-flex items-center gap-1 disabled:opacity-60">
              {uploading ? <Loader2 size={11} className="animate-spin"/> : <ImagePlus size={11}/>} Replace
            </button>
            <button type="button" onClick={() => onChange('')} data-testid={`${testid}-remove`}
              className="text-[11px] px-2 py-1 rounded bg-black/70 border border-[#3a3a3a] text-white hover:border-red-500 inline-flex items-center gap-1">
              <Trash2 size={11}/> Remove
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
